import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ShieldCheckIcon, AlertCircleIcon, CoinsIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { verifyUserAge } from '@/lib/api/profile';
import Button from '@/components/form/Button';
import { Link } from 'react-router-dom';

export default function AgeVerification() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleVerifyAge = async () => {
    try {
      setLoading(true);
      
      // Call the API function to verify the user's age
      await verifyUserAge();
      
      toast.success('Age verification successful!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error verifying age:', error);
      toast.error('Failed to verify age');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <Button
        variant="ghost"
        size="sm"
        icon={<ArrowLeftIcon className="h-4 w-4" />}
        onClick={() => navigate('/profile')}
        className="mb-6"
      >
        Back to Profile
      </Button>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
          <h1 className="text-2xl font-bold text-white flex items-center">
            <ShieldCheckIcon className="h-6 w-6 mr-2" />
            Age Verification
          </h1>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircleIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-yellow-800">Age Verification Required</h3>
                <div className="mt-2 text-yellow-700">
                  <p>You must verify that you are at least 18 years old to participate in CashSNAPs and withdraw earnings.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center mb-4">
              <CoinsIcon className="h-8 w-8 text-yellow-500 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">CashSNAP Participation</h2>
            </div>
            
            <p className="text-gray-600 mb-6">
              CashSNAPs allow you to earn real money by correctly predicting outcomes. To ensure compliance with regulations:
            </p>
            
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>You must be at least 18 years old to participate</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Age verification is required before withdrawing earnings</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span>Verification is handled securely through Stripe</span>
              </li>
            </ul>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Coming Soon: Stripe Verification</h3>
              <p className="text-gray-600 text-sm">
                We're currently implementing Stripe for secure age verification. For now, you can self-verify by confirming you are at least 18 years old.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleVerifyAge}
                loading={loading}
                className="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
              >
                <ShieldCheckIcon className="h-5 w-5 mr-2" />
                Verify I am 18+ years old
              </Button>
              
              <Button
                variant="outline"
                onClick={() => navigate('/profile')}
              >
                Return to Profile
              </Button>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            <p>
              By verifying your age, you confirm that you are at least 18 years old and eligible to participate in CashSNAPs according to our <Link to="/conditions" className="text-blue-600 hover:underline">Conditions of Participation</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}